import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notificationApi } from "./api";

export function useNotifications(page = 0, size = 10) {
  return useQuery({
    queryKey: ["notifications", page, size],
    queryFn: () => notificationApi.list(page, size),
  });
}

export function useUnreadNotificationsCount() {
  return useQuery({
    queryKey: ["notifications-unread-count"],
    queryFn: notificationApi.unreadCount,
    staleTime: 1000 * 30,
  });
}

function invalidateNotifQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ["notifications"] });
  queryClient.invalidateQueries({ queryKey: ["notifications-unread-count"] });
  // also refresh the topbar badge (uses a separate query key)
  queryClient.invalidateQueries({ queryKey: ["notif-unread-count"] });
  queryClient.invalidateQueries({ queryKey: ["dashboard"] });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (messageId: number) => notificationApi.markRead(messageId),
    // Optimistically flip the item to READ immediately so the UI updates
    // without waiting for a round-trip refetch.
    onMutate: async (messageId: number) => {
      await queryClient.cancelQueries({ queryKey: ["notifications"] });
      const snapshots = queryClient.getQueriesData({ queryKey: ["notifications"] });

      queryClient.setQueriesData(
        { queryKey: ["notifications"] },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (old: any) => {
          if (!old?.content) return old;
          return {
            ...old,
            content: old.content.map((n: { id: number; status?: string; readAt?: string | null }) =>
              n.id === messageId
                ? { ...n, status: "READ", readAt: new Date().toISOString() }
                : n
            ),
          };
        }
      );
      return { snapshots };
    },
    onError: (_err, _id, context) => {
      // Roll back on failure
      context?.snapshots?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
    onSettled: () => invalidateNotifQueries(queryClient),
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => notificationApi.markAllRead(),
    onSuccess: () => invalidateNotifQueries(queryClient),
  });
}